/*
 * ===============================================================================================================
 *                                Copyright 2021, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import _ from 'lodash';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import AddPolicyDialog from '../AddPolicyDialog';
import { deletePolicyForRole, getPoliciesForRole } from '../../services/policy';
import { IPolicy } from '../../models/Policies/PolicyContract';
import { Box, Grid, Typography, IconButton, Menu, Button, TableCellProps } from '@mui/material';
import {
  LuiCheckboxList,
  LuiSearch,
  LuiActionsMenu,
  LuiToolbar,
  LuiEmptyState,
  LuiMaterialTableHeadCellStyles
} from '@jda/lui-common-component-library-mui5';
import {
  MRT_Row,
  MRT_RowData,
  MRT_ToggleFullScreenButton as ToggleFullScreenButton,
  MRT_ShowHideColumnsButton as ShowHideColumnsButton,
  MRT_ToggleDensePaddingButton as ToggleDensePaddingButton
} from 'material-react-table';
import { useStyles } from './styles';
import SnackbarAlert from '../../shared/SnackbarAlert';
import { ErrorMessage } from '../../shared/ErrorMessage/ErrorMessage';
import { IErrorMessage } from '../../models/Common/ErrorMessage';
import {
  DELETE_TITLE,
  DIALOUGE_CANCEL,
  DIALOUGE_CONTENT,
  DIALOUGE_OK,
  EMPTY_SCREEN_TEXT,
  SEARCH,
  SEARCH_POLICIES_MAX_LENGTH,
  SNACKBAR_ALERT_DEFAULT_PROPS,
  ADD_RULE,
  PERMISSION_SAVED_SUCCESSFULY,
  PERMISSION_ALREADY_EXIST,
  PERMISSION_REMOVED_ERROR,
  ERROR_FETCHING_DATA,
  ROWS_PER_PAGE_LABEL,
  NOT_APPLICABLE,
  FILTER_BY_POLICY_TYPE,
  EDIT,
  DELETE,
  POLICY_TYPE_KEYS,
  POLICY_TYPE_LABELS,
  POLICY_TYPE_LABELS_WITHOUT_DIMENSION_V2
} from './constants';
import {
  FUNCTIONAL,
  DATA,
  ENTITIES,
  DIMENSION,
  DIMENSIONV2,
  MEASURE,
  POLICY_SYNTAX_VERSION_V2,
  DISPLAY_NAME
} from '../../shared/PolicyDialog/constants';
import { IRoleSelected } from '../../models/Roles/RolesContracts';
import { IPermission } from '../../models/Permission/PermissionContract';
import { deletePermissionForRole, getPermissionsForRole, savePermissionOfRole } from '../../services/permissions';
import { Filter, ReportAdd, Trash } from '@jda/lui-common-icon-library-mui5';
import { Edit } from '@jda/lui-common-icon-library-mui5';
import { AlertDialog } from '../../shared/Dialog/AlertDialog';
import { CheckViewPermission } from '../../shared/CheckPermissions/CheckViewPermission';
import { permissions as permissionConfig } from '../../shared/CheckPermissions/constants';
import { useDispatch, useSelector } from 'react-redux';
import { selectAppLocation } from '../../store/appLocation/appLocationSelector';
import { selectAllAssignedPermissions } from '../../store/auth/authSelector';
import { checkPermission } from '../../shared/CheckPermissions/CheckPermission';
import TableWithPaginator from '../../shared/TableWithPaginator';
import { isArray } from 'lodash';
import { setSnackBarMessage } from '../../store/snackBar/snackBarActionCreator';
import { resetPolicyFormData } from '../../store/role/PolicyData/dataActionCreator';
import { compareByProperty } from '../../utility/commonUtility';
import PolicyRuleDialog from './PolicyRuleDialog';
import { getEntityPolicyRule } from '../../shared/PolicyDialog/PolicyUtils';
import { getFeatureFlag as useFeatureFlag } from '@jda/lui-portal-utilities';
import { HARNESS_FEATURE_FLAG } from '../../utility/constants';
import useWindowDimensions from '../../utility/useWindowDimensions';

const displayTypeMap = new Map(POLICY_TYPE_LABELS.map(description => [description.key, description.displayName]));

interface PolicyTypeFilterProps {
  value: any[];
  onChange: (value: any[]) => void;
}

export const PolicyTypeFilter = ({ value, onChange }: PolicyTypeFilterProps) => {
  const { classes } = useStyles();
  const isDimentionV2PolicyEnabled = useFeatureFlag(HARNESS_FEATURE_FLAG.dimentionV2);
  const listOfPolicies = isDimentionV2PolicyEnabled ? POLICY_TYPE_LABELS : POLICY_TYPE_LABELS_WITHOUT_DIMENSION_V2;
  const [anchorEl, setAnchorEl] = useState<HTMLElement>();
  return (
    <>
      <IconButton
        aria-label={FILTER_BY_POLICY_TYPE}
        aria-controls={anchorEl ? 'policy-type-filter' : undefined}
        aria-haspopup="true"
        color={value.length ? 'primary' : undefined}
        onClick={event => {
          setAnchorEl(event.currentTarget);
        }}
      >
        <Filter />
      </IconButton>
      {anchorEl && (
        <Menu
          open={true}
          anchorEl={anchorEl}
          id="policy-type-filter"
          onClose={() => {
            setAnchorEl(undefined);
          }}
          classes={{ paper: classes.policyTypeDropdown }}
        >
          <LuiCheckboxList
            width={400}
            itemKey="key"
            size="small"
            hideLabelName
            selectedItems={value}
            data={listOfPolicies}
            itemDisplayName={DISPLAY_NAME}
            labelName={FILTER_BY_POLICY_TYPE}
            className={classes.selectAllFilter}
            dataTestId="policy-type-checkbox-filter"
            onSelectionChange={(_event, selectedItems) => {
              onChange(selectedItems);
            }}
          />
        </Menu>
      )}
    </>
  );
};

const Policies = ({ selectedItem }: IRoleSelected) => {
  const appLocation = useSelector(selectAppLocation);
  const { height } = useWindowDimensions();
  const [permissions, setPermissions] = useState<Array<IPermission>>([]);
  const [policies, setPolicies] = useState<Array<IPolicy>>([]);
  const [inputValue, setInputValue] = useState('');
  const [showLoader, showHideLoader] = useState(true);
  const [errors, setErrors] = useState<IErrorMessage>({ message: '' });
  const [isPreviewShown, setPreviewShown] = useState(false);
  const [showDialogLoader, setDialogHideLoader] = useState(false);
  const [rowData, setRowData] = useState<IPolicy | IPermission | any>();
  const [openModal, setOpenCloseModal] = useState(false);
  const [selectedPolicyTypeFilter, setSelectedPolicyTypeFilter] = useState<any[]>([]);
  const [isEdit, setIsEdit] = useState(false);
  const [openJsonViewer, setOpenJsonViewer] = useState(false);
  const isDimentionV2PolicyEnabled = useFeatureFlag(HARNESS_FEATURE_FLAG.dimentionV2);

  useEffect(() => {
    setPolicies([]);
    setPermissions([]);
  }, []);

  const tableData = useMemo(() => {
    let filteredPoliciesList: any[] = [];
    [...permissions, ...policies].forEach(p => {
      selectedPolicyTypeFilter.forEach((policyTypeList: any) => {
        if (p.policyType === policyTypeList.key) {
          filteredPoliciesList.push(p);
        }
      });
    });
    if (!selectedPolicyTypeFilter.length) {
      filteredPoliciesList = [...permissions, ...policies];
    }
    if (filteredPoliciesList.length) {
      var results = _.filter(filteredPoliciesList, function(obj) {
        return obj.displayName.indexOf(inputValue) !== -1;
      });
      return results.sort(compareByProperty('policyType'));
    }
    return [];
  }, [permissions, policies, selectedPolicyTypeFilter, inputValue]);

  const dispatch = useDispatch();

  const assignedPermissions = useSelector(selectAllAssignedPermissions);
  const writePolicyPermission =
    assignedPermissions &&
    assignedPermissions.permissions &&
    checkPermission([permissionConfig.policy.read_assign], assignedPermissions.permissions);

  const { classes } = useStyles();
  const columns: any = [
    {
      header: 'Policy Type',
      accessorKey: 'policyType',
      Cell: ({ row }: { row: MRT_Row<MRT_RowData> }) => {
        const { policyType, expression } = row.original;
        const displayName = displayTypeMap.get(policyType);

        if ([DIMENSION, DIMENSIONV2].includes(policyType)) {
          const policyExpression = JSON.parse(expression);

          return <Typography variant='body1'>{`${displayName}-(${policyExpression.syntaxVersion})`}</Typography>;
        }

        return <Typography variant='body1'>{displayName}</Typography>;
      }
    },
    {
      header: 'Name',
      accessorKey: 'displayName',
      muiTableHeadCellProps: ({ column, table }) =>
        ({
          sx: theme => ({
            ...LuiMaterialTableHeadCellStyles({
              column,
              theme,
              table,
              styling: 'standard',
              virtualizationEnabled: false
            }),
            marginRight: 10
          })
        } as TableCellProps)
    },
    {
      header: 'Description',
      accessorKey: 'description'
    },
    {
      header: '',
      accessorKey: 'expression',
      muiTableBodyCellProps: {
        sx: {
          overflowWrap: 'anywhere'
        }
      },
      Cell: ({ row }: { row: MRT_Row<any> }) => {
        return (
          <>
            {row.original.policyType !== 'Functional' && (
              <Box data-testid="toolTip-sign">
                <Box id="jsonViewer">
                  <Typography
                    color="primary"
                    onClick={() => setJsonViewerData(row.original)}
                    className={classes.viewPolicyText}
                    data-testid="view-policy"
                  >
                    View Policy
                  </Typography>
                </Box>
              </Box>
            )}
          </>
        );
      }
    }
  ];
  const actions: any = (row: MRT_Row<any>) => {
    return (
      <LuiActionsMenu
        items={[
          Boolean(
            appLocation ||
              !writePolicyPermission ||
              ![DATA, ENTITIES, DIMENSIONV2].includes(row.original.policyType) ||
              (row.original.policyType === DIMENSIONV2 && !isDimentionV2PolicyEnabled)
          )
            ? []
            : {
                render: (
                  <IconButton
                    aria-label={EDIT}
                    onClick={() => {
                      editPolicy(row.original);
                    }}
                    size="small"
                  >
                    <Edit />
                  </IconButton>
                ),
                alwaysVisible: true
              },
          {
            render: (
              <IconButton
                aria-label={DELETE}
                onClick={() => {
                  deletePolicyOnClick(row.original);
                }}
                size="small"
              >
                <Trash />
              </IconButton>
            ),
            alwaysHidden: Boolean(appLocation || !writePolicyPermission)
          }
        ].flat()}
        maxItemsToDisplay={2}
      />
    );
  };
  const deletePolicyOnClick = (rowData: IPolicy | IPermission) => {
    setPreviewShown(true);
    setIsEdit(false);
    setRowData(rowData);
  };
  const deleteRole = async () => {
    setDialogHideLoader(true);
    rowData && (rowData.policyType === FUNCTIONAL ? removePermission(rowData) : removePolicy(rowData));
  };
  const editPolicy = (rowData: IPolicy | IPermission) => {
    setOpenCloseModal(!openModal);
    setIsEdit(true);
    setRowData(rowData);
  };
  const setJsonViewerData = (rowData: IPolicy) => {
    setOpenJsonViewer(!openJsonViewer);
    setRowData(rowData);
  };
  const savePermission = (permissionId: number) => {
    setErrors({ message: '' });
    savePermissionOfRole(selectedItem.id, { permissionId: permissionId })
      .then(() => {
        showHideLoader(false);
        dispatch(
          setSnackBarMessage({
            ...SNACKBAR_ALERT_DEFAULT_PROPS,
            message: `${PERMISSION_SAVED_SUCCESSFULY}`,
            severity: 'success'
          })
        );
        getAllPermissionForRole();
      })
      .catch(error => {
        showHideLoader(false);
        dispatch(
          setSnackBarMessage({
            ...SNACKBAR_ALERT_DEFAULT_PROPS,
            message: `${PERMISSION_ALREADY_EXIST}` + error.message,
            severity: 'error'
          })
        );
      });
  };

  const removePermission = async (rowData: IPermission | IPolicy) => {
    setErrors({ message: '' });
    try {
      await deletePermissionForRole(selectedItem.id, rowData.id);
      getAllPermissionForRole();
      showHideLoader(false);
      setDialogHideLoader(false);
      setPreviewShown(false);
      dispatch(
        setSnackBarMessage({
          ...SNACKBAR_ALERT_DEFAULT_PROPS,
          message: `Policy ${rowData.displayName} removed from ${selectedItem.name}`,
          severity: 'success'
        })
      );
    } catch (error) {
      showHideLoader(false);
      setDialogHideLoader(false);
      dispatch(
        setSnackBarMessage({
          ...SNACKBAR_ALERT_DEFAULT_PROPS,
          message: `${PERMISSION_REMOVED_ERROR}` + error,
          severity: 'error'
        })
      );
    }
  };

  const removePolicy = async (rowData: IPolicy | IPermission) => {
    setErrors({ message: '' });
    try {
      await deletePolicyForRole(rowData.id, selectedItem.id);
      const updatedPolicy = policies.filter(p => p.id !== rowData.id);
      setPolicies(updatedPolicy);
      showHideLoader(false);
      setDialogHideLoader(false);
      setPreviewShown(false);
      dispatch(
        setSnackBarMessage({
          ...SNACKBAR_ALERT_DEFAULT_PROPS,
          message: `Policy ${rowData.displayName} has been deleted and removed from ${selectedItem.name}`,
          severity: 'success'
        })
      );
      getAllPoliciesForRoles();
    } catch (error) {
      showHideLoader(false);
      setDialogHideLoader(false);
      dispatch(
        setSnackBarMessage({
          ...SNACKBAR_ALERT_DEFAULT_PROPS,
          message: 'Error in removing policy: ' + error,
          severity: 'error'
        })
      );
    }
  };

  const getAllPermissionForRole = useCallback(() => {
    setErrors({ message: '' });
    getPermissionsForRole(selectedItem.id, appLocation?.name)
      .then((response: IPermission[]) => {
        if (response.length > 0) {
          showHideLoader(true);
          const mappedPermissions = response.map(permission => {
            return {
              ...permission,
              policyType: FUNCTIONAL,
              rule: permission.name
            };
          });
          setPermissions(mappedPermissions);
        } else {
          setPermissions([]);
        }
        showHideLoader(false);
      })
      .catch((_error: any) => {
        const errorMessage = {
          message: ERROR_FETCHING_DATA
        };
        setErrors(errorMessage);
        showHideLoader(false);
      });
  }, [selectedItem.id, appLocation]);

  const getDataPolicyRule = (policyExpression: any) => {
    const { modules, resource, policy_rules, actions } = policyExpression;
    return `Module=${modules.map((item: { name: string }) => item.name) || NOT_APPLICABLE}; 
        Resource=${(resource && resource.keys && resource.keys[1].hierarchyId) || NOT_APPLICABLE}; 
        Product Hierarchy=${(policy_rules &&
          policy_rules.values &&
          policy_rules.values.length > 0 &&
          policy_rules.values.flatMap((itemValue: { [x: string]: string }) =>
            Object.keys(itemValue).flatMap(itemKeys => itemValue[itemKeys])
          )) ||
          NOT_APPLICABLE}; 
      Action=${(actions && actions.length > 0 && isArray(actions) && actions.join(',')) || NOT_APPLICABLE}`;
  };

  const getDimensionPolicyRule = (policyExpression: any) => {
    const {
      namespace,
      policy: {
        dimension: { dimensionId, hierarchyId, hierarchyFilter },
        actions
      }
    } = policyExpression;
    return `Namespace=${namespace};Dimension={dimensionId=${dimensionId};hierarchyId=${hierarchyId};
        hierarchyFilter={${Object.keys(hierarchyFilter).length > 0 &&
          Object.keys(hierarchyFilter)
            .map((item: any) =>
              Array.isArray(hierarchyFilter[item])
                ? `${[item]} = ${hierarchyFilter[item].toString()}`
                : `${[item]} = ${hierarchyFilter[item]}`
            )
            .join(';')}};Allowed Actions=${(actions && actions.length && actions.join(',')) || NOT_APPLICABLE}`;
  };
  const getMeasurePolicyRule = (policyExpression: any) => {
    const measureActions = policyExpression.policy.actions;
    const measureGroup = policyExpression.policy.measures ? policyExpression.policy.measures.measureGroup : null;
    const measuresList = policyExpression.policy.measures
      ? `Measures=${policyExpression.policy.measures.measures.map((item: string) => item) || NOT_APPLICABLE};`
      : '';

    return `Measure Group=${
      measureGroup ? measureGroup : policyExpression.policy.measureGroups.map((item: string) => item) || NOT_APPLICABLE
    }; 
            ${measuresList}Allowed Actions=${(measureActions && measureActions.length && measureActions.join(',')) ||
      NOT_APPLICABLE}`;
  };
  const getDimensionPolicyV2Rule = (policyExpression: any) => {
    const {
      namespace,
      policy: {
        dimension: {
          dimensionId,
          actions: dimensionActions,
          dataAccess,
          hierarchyAccess: { hierarchies }
        }
      }
    } = policyExpression;

    return `Namespace=${namespace};
            {
              Dimension= ${dimensionId}, 
              Actions = [${dimensionActions.join(',')}]
            };
            ${
              hierarchies?.length > 0
                ? `{
              Hierarchy = [${hierarchies
                .map(
                  (hierarchy: any) =>
                    `{ 
                hierarchyId=${hierarchy.id}, 
                depthAccess=${hierarchy.depthAccess}, 
                Action=[${hierarchy.actions.map((action: any) => `${action}`).join(',')}]
              }`
                )
                .join(',') || NOT_APPLICABLE}]
            };`
                : ''
            }        
            ${
              dataAccess?.hierarchyId
                ? `{
              DataAccess = [{
                  hierarchyId=${dataAccess?.hierarchyId}, 
                  hierarchyFilter={${Object.entries(dataAccess?.hierarchyFilter).length > 0 &&
                    Object.entries(dataAccess?.hierarchyFilter)
                      .map(
                        ([key, value]) =>
                          `${key} = ${Array.isArray(value) ? `[${value.map(val => `${val}`).join(',')}]` : `${value}`}`
                      )
                      .join(',')}}, 
                  allowedActions = [${dataAccess?.actions.map((action: any) => `${action}`).join(',') ||
                    NOT_APPLICABLE}] 
                }] 
            };`
                : ''
            }`;
  };

  const getAllMappedPolicies = (response: IPolicy[]) => {
    return response.map(policy => {
      const policyExpression = JSON.parse(policy.expression);

      const type = policyExpression?.policy_type || policyExpression?.policyType;
      let policyObj: any = {};

      switch (type) {
        case POLICY_TYPE_KEYS.DATA:
          policyObj = {
            ...policy,
            policyType: DATA,
            rule: getDataPolicyRule(policyExpression),
            policyExpression
          };
          break;
        case POLICY_TYPE_KEYS.ENTITY:
          policyObj = {
            ...policy,
            policyType: ENTITIES,
            rule: getEntityPolicyRule(policyExpression),
            policyExpression
          };
          break;
        case POLICY_TYPE_KEYS.DIMENSION:
          policyObj = {
            ...policy,
            policyType: policyExpression.syntaxVersion === POLICY_SYNTAX_VERSION_V2 ? DIMENSIONV2 : DIMENSION,
            rule:
              policyExpression.syntaxVersion === POLICY_SYNTAX_VERSION_V2
                ? getDimensionPolicyV2Rule(policyExpression)
                : getDimensionPolicyRule(policyExpression),
            policyExpression
          };
          break;
        case POLICY_TYPE_KEYS.MEASURE_GROUP:
        case POLICY_TYPE_KEYS.MEASURE:
          policyObj = {
            ...policy,
            policyType: MEASURE,
            rule: getMeasurePolicyRule(policyExpression),
            policyExpression
          };
          break;
      }
      return policyObj;
    });
  };
  const getAllPoliciesForRoles = useCallback(() => {
    setErrors({ message: '' });
    getPoliciesForRole(selectedItem.id, appLocation?.name)
      .then((response: IPolicy[]) => {
        if (response.length > 0) {
          showHideLoader(true);
          let mappedPolicies: any;
          try {
            mappedPolicies = getAllMappedPolicies(response);
            setPolicies(mappedPolicies);
          } catch (error) {
            const errorMessage = {
              message: 'Incorrect Policy Schema: ' + error
            };
            setErrors(errorMessage);
          }
        } else {
          setPolicies([]);
        }
        showHideLoader(false);
      })
      .catch(error => {
        const errorMessage = {
          message: 'Error in fetching data: ' + error
        };
        setErrors(errorMessage);
        showHideLoader(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItem.id, appLocation]);

  useEffect(() => {
    getAllPoliciesForRoles();
    getAllPermissionForRole();
  }, [getAllPoliciesForRoles, getAllPermissionForRole]);

  useEffect(() => {
    setSelectedPolicyTypeFilter([]);
  }, [selectedItem.id]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleOnClear = () => {
    setInputValue('');
  };

  const displaySnackbar = (message: string, severity: 'success' | 'error') => {
    dispatch(
      setSnackBarMessage({
        ...SNACKBAR_ALERT_DEFAULT_PROPS,
        message: message,
        severity: severity
      })
    );
  };

  const resetDefaultFormValues = () => {
    dispatch(resetPolicyFormData());
  };
  const handleJsonViewerClose = () => {
    setOpenJsonViewer(false);
  };

  const emptyTableFallback = () => {
    return (
      <Box my={5}>
        <LuiEmptyState imageItem={null} subHeading={EMPTY_SCREEN_TEXT} />
      </Box>
    );
  };
  const extendedToolbar = ({ table }) => {
    const leftComponents = {
      component: (
        <Grid item xs={10} className={classes.filterBlock}>
          <LuiSearch
            size="small"
            id="policy-details-search-bar"
            value={inputValue}
            onChange={handleSearchChange}
            onClear={handleOnClear}
            placeholder={SEARCH}
            className={classes.searchInput}
            inputProps={{
              maxLength: SEARCH_POLICIES_MAX_LENGTH
            }}
          />
          <PolicyTypeFilter value={selectedPolicyTypeFilter} onChange={setSelectedPolicyTypeFilter} />
        </Grid>
      )
    };
    const rightComponents = {
      component: (
        <Box>
          {!appLocation && (
            <CheckViewPermission permission={[permissionConfig.policy.read_assign]}>
              <Button
                size="small"
                variant="contained"
                id="add-policy-rule-button"
                startIcon={<ReportAdd />}
                className={classes.margin}
                onClick={() => {
                  setOpenCloseModal(!openModal);
                  setRowData(null);
                  setIsEdit(false);
                  resetDefaultFormValues();
                }}
              >
                {ADD_RULE}
              </Button>
            </CheckViewPermission>
          )}
          <ShowHideColumnsButton table={table} />
          <ToggleDensePaddingButton table={table} />
          <ToggleFullScreenButton table={table} />
        </Box>
      )
    };
    return <LuiToolbar className={classes.toolbarPanel} leftZone={leftComponents} rightZone={rightComponents} />;
  };

  return (
    <Grid container>
        <Grid id="policy-card-component-container" item xs={12} sm={12}>
          <Grid className={classes.card}>
            <TableWithPaginator
              data={tableData}
              columns={columns}
              loading={showLoader}
              customRowActions={actions}
              extendedToolbar={extendedToolbar}
              renderEmptyState={emptyTableFallback}
              maxTableBodyHeight={height - 350}
              labelRowsPerPage={ROWS_PER_PAGE_LABEL}
              defaultItemsPerPage={tableData.length >= 80 ? 50 : 20}
              rowsPerPageOptions={tableData.length >= 80 ? [50, 100, 200] : [20, 50, 100]}
            />
          </Grid>
      </Grid>
      {isPreviewShown && (
        <AlertDialog
          dialogTitle={DELETE_TITLE}
          dialogContentText={DIALOUGE_CONTENT}
          recordName={(rowData && rowData.displayName) || ''}
          primaryButtonText={DIALOUGE_OK}
          primaryButtonColor='error'
          secondButtonText={DIALOUGE_CANCEL}
          handleOnCancel={() => {
            setPreviewShown(false);
            setIsEdit(false);
          }}
          handleOnOk={deleteRole}
          showDialogLoader={showDialogLoader}
        />
      )}
      {openModal && (
        <AddPolicyDialog
          onClose={() => {
            setOpenCloseModal(!openModal);
          }}
          role={selectedItem}
          refreshPolicyOnSave={getAllPoliciesForRoles}
          onSave={savePermission}
          edit={isEdit}
          rowData={rowData}
        />
      )}
      {openJsonViewer && (
        <PolicyRuleDialog
          rowData={rowData}
          open={openJsonViewer}
          handleClose={handleJsonViewerClose}
          displaySnackbar={displaySnackbar}
        />
      )}
      <SnackbarAlert />
      {errors && <ErrorMessage {...errors} />}
    </Grid>
  );
};

export default Policies;
